/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRiskPoliciesRequest,
  ListRiskPoliciesSecurity,
} from "../models/operations/listriskpolicies.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskListPoliciesQuery,
  prefetchRiskListPolicies,
  queryKeyRiskListPolicies,
  RiskListPoliciesQueryData,
} from "./riskListPolicies.core.js";
export {
  buildRiskListPoliciesQuery,
  prefetchRiskListPolicies,
  queryKeyRiskListPolicies,
  type RiskListPoliciesQueryData,
};

export type RiskListPoliciesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRiskPolicies risk
 *
 * @remarks
 * List all risk analysis policies for the current project.
 */
export function useRiskListPolicies(
  request?: ListRiskPoliciesRequest | undefined,
  security?: ListRiskPoliciesSecurity | undefined,
  options?: QueryHookOptions<
    RiskListPoliciesQueryData,
    RiskListPoliciesQueryError
  >,
): UseQueryResult<RiskListPoliciesQueryData, RiskListPoliciesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskListPoliciesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRiskPolicies risk
 *
 * @remarks
 * List all risk analysis policies for the current project.
 */
export function useRiskListPoliciesSuspense(
  request?: ListRiskPoliciesRequest | undefined,
  security?: ListRiskPoliciesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskListPoliciesQueryData,
    RiskListPoliciesQueryError
  >,
): UseSuspenseQueryResult<
  RiskListPoliciesQueryData,
  RiskListPoliciesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskListPoliciesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskListPoliciesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskListPoliciesQueryData,
): RiskListPoliciesQueryData | undefined {
  const key = queryKeyRiskListPolicies(...queryKeyBase);

  return client.setQueryData<RiskListPoliciesQueryData>(key, data);
}

export function invalidateRiskListPolicies(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "policies", "list", ...queryKeyBase],
  });
}

export function invalidateAllRiskListPolicies(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "policies", "list"],
  });
}
